/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Gasteiger-Marsili partial atomic charges (PEOE — Partial Equalization of
 * Orbital Electronegativity). Pure JS, deterministic, no external deps.
 *
 * Reference: Gasteiger, J.; Marsili, M. "Iterative partial equalization of
 *   orbital electronegativity — a rapid access to atomic charges."
 *   Tetrahedron 1980, 36, 3219-3228.
 *
 * Algorithm:
 *   1. Infer each atom's hybridization from its bond pattern.
 *   2. Look up (a, b, c, χ⁺) parameters for that (element, hybridization).
 *   3. Iterate (default 6 rounds), each round:
 *      - χᵢ = aᵢ + bᵢ·qᵢ + cᵢ·qᵢ²        (current orbital electronegativity)
 *      - For each bond i-j: charge flows from the less electronegative atom
 *        (donor) to the more electronegative one (acceptor) by an amount
 *        Δq = (|χⱼ − χᵢ| / χ⁺_donor) · damping^iter.
 *
 * Caveats:
 *   - Aromatic bonds (order 4) are treated as single bonds for transfer
 *     accounting but trigger sp² hybridization, which is the conventional
 *     simplification.
 *   - Transition metals are unparameterized; bonds touching them are skipped.
 *   - Implicit hydrogens are *not* materialized — run AddHydrogens first if
 *     the SDF only contains heavy atoms.
 *   - Formal charges are honored by seeding qᵢ with the input atom's
 *     formalCharge (matches the original 1980 paper). Polarization then
 *     redistributes a fraction of that charge onto neighbors each
 *     iteration, so an `N⁺` ends near +0.9 with adjacent atoms picking up
 *     small positive shares.
 */

import {
  MoleculeAtom,
  MoleculeBond,
  parseLooseSdf,
  readSdfPartialCharges,
  writeSdfPartialCharges,
} from './sdfNormalize.ts';

interface PeoeParams {
  a: number; // electronegativity of neutral atom (eV)
  b: number; // first-derivative coefficient
  c: number; // second-derivative coefficient
  ePlus: number; // electronegativity of cation, used as transfer ceiling
}

// Gasteiger 1980 Table 1 parameters, indexed by (element, hybridization).
// Single-state atoms (H, halogens) use the wildcard '*' key.
const PEOE_PARAMS: Record<string, Record<string, PeoeParams>> = {
  H: { '*': { a: 7.17, b: 6.24, c: -0.56, ePlus: 20.02 } },
  C: {
    sp3: { a: 7.98, b: 9.18, c: 1.88, ePlus: 19.04 },
    sp2: { a: 8.79, b: 9.32, c: 1.51, ePlus: 19.62 },
    sp: { a: 10.39, b: 9.45, c: 0.73, ePlus: 20.57 },
  },
  N: {
    sp3: { a: 11.54, b: 10.82, c: 1.36, ePlus: 23.72 },
    sp2: { a: 12.87, b: 11.15, c: 0.85, ePlus: 24.87 },
    sp: { a: 15.68, b: 11.7, c: -0.27, ePlus: 27.11 },
  },
  O: {
    sp3: { a: 14.18, b: 12.92, c: 1.39, ePlus: 28.49 },
    sp2: { a: 17.07, b: 13.79, c: 0.47, ePlus: 31.33 },
  },
  F: { '*': { a: 14.66, b: 13.85, c: 2.31, ePlus: 30.82 } },
  Cl: { '*': { a: 11.0, b: 9.69, c: 1.35, ePlus: 22.04 } },
  Br: { '*': { a: 10.08, b: 8.47, c: 1.16, ePlus: 19.71 } },
  I: { '*': { a: 9.9, b: 7.96, c: 0.96, ePlus: 18.82 } },
  S: {
    sp3: { a: 10.14, b: 9.13, c: 1.38, ePlus: 20.65 },
    sp2: { a: 10.88, b: 9.49, c: 1.33, ePlus: 21.7 },
  },
  P: { sp3: { a: 8.9, b: 8.24, c: 0.96, ePlus: 18.1 } },
};

const inferHybridization = (atomIdx: number, bonds: MoleculeBond[]): 'sp' | 'sp2' | 'sp3' => {
  let maxOrder = 1;
  for (const b of bonds) {
    if (b.a !== atomIdx && b.b !== atomIdx) continue;
    if (b.order === 3) return 'sp';
    if (b.order > maxOrder && b.order !== 4) maxOrder = b.order;
    if (b.order === 4 && maxOrder < 2) maxOrder = 2; // aromatic ⇒ sp²
  }
  return maxOrder >= 2 ? 'sp2' : 'sp3';
};

const lookupParams = (element: string, hyb: 'sp' | 'sp2' | 'sp3'): PeoeParams | null => {
  const table = PEOE_PARAMS[element];
  if (!table) return null;
  return table[hyb] ?? table['*'] ?? table.sp3 ?? null;
};

export interface GasteigerOptions {
  iterations?: number; // default 6
  damping?: number; // default 0.5 (per-iter factor: damping^iter)
}

/**
 * Compute Gasteiger-Marsili partial charges for an atom/bond list.
 * Output array is index-aligned with `atoms`. Atoms whose element is not
 * parameterized (e.g. transition metals) get charge 0 and are excluded
 * from charge propagation along their bonds.
 */
export const assignGasteigerCharges = (
  atoms: MoleculeAtom[],
  bonds: MoleculeBond[],
  opts: GasteigerOptions = {},
): number[] => {
  const n = atoms.length;
  const iterations = opts.iterations ?? 6;
  const damping = opts.damping ?? 0.5;

  const params: (PeoeParams | null)[] = atoms.map((atom, i) =>
    lookupParams(atom.element, inferHybridization(i, bonds)),
  );

  const validBonds: MoleculeBond[] = bonds.filter(
    (b) => b.a >= 0 && b.b >= 0 && b.a < n && b.b < n && params[b.a] && params[b.b],
  );

  // Seed qᵢ with the input atom's formal charge (defaulting to 0). The
  // PEOE iteration polarizes on top of this seed, so a +1 amine starts at
  // +1 and ends slightly below +1 with the rest of the +1 spread onto
  // neighbors — which is what the original Gasteiger-Marsili paper does
  // and what downstream MD/EPS code expects when reading the partial
  // charges of a charged species.
  const q = atoms.map((a) => (Number.isFinite(a.formalCharge as number) ? (a.formalCharge as number) : 0));

  for (let iter = 1; iter <= iterations; iter++) {
    const factor = Math.pow(damping, iter);
    const chi = q.map((qi, i) => {
      const p = params[i];
      return p ? p.a + p.b * qi + p.c * qi * qi : 0;
    });
    const dq = new Array<number>(n).fill(0);
    for (const b of validBonds) {
      const ci = chi[b.a];
      const cj = chi[b.b];
      if (ci === cj) continue;
      const donor = ci < cj ? b.a : b.b;
      const acceptor = ci < cj ? b.b : b.a;
      const ceiling = params[donor]!.ePlus;
      if (ceiling <= 0) continue;
      const transfer = (Math.abs(cj - ci) / ceiling) * factor;
      dq[donor] += transfer;
      dq[acceptor] -= transfer;
    }
    for (let i = 0; i < n; i++) q[i] += dq[i];
  }

  return q;
};

/**
 * Parse an SDF and compute Gasteiger charges in one call.
 * Returns null if no atoms can be parsed.
 */
export const computeGasteigerChargesForSdf = (sdf: string): number[] | null => {
  const mol = parseLooseSdf(sdf);
  if (!mol || mol.atoms.length === 0) return null;
  return assignGasteigerCharges(mol.atoms, mol.bonds);
};

/**
 * Walk a list of molecules and ensure each one has partial charges available.
 * For each molecule:
 *   - if `atomCharges` is already populated, leave it alone
 *   - else if the SDF carries a `<PartialCharges>` tag, lift it into atomCharges
 *   - else compute Gasteiger charges fresh, populate atomCharges, and bake the
 *     charges into the SDF so the next save/load round-trip is a no-op.
 *
 * Mutates molecules in place. Used by project load paths (cloud + persist
 * middleware) so opening a project always shows partial charges.
 */
export const rehydratePartialChargesFromSdf = <T extends { data?: string; atomCharges?: number[] }>(
  molecules: T[] | undefined | null,
): void => {
  if (!molecules) return;
  for (const m of molecules) {
    if (!m.data || (m.atomCharges && m.atomCharges.length > 0)) continue;
    let charges = readSdfPartialCharges(m.data);
    if (!charges) {
      // Neither in memory nor in SDF — compute fresh and bake the result back
      // into the SDF so subsequent loads can take the cheap rehydrate path.
      const fresh = computeGasteigerChargesForSdf(m.data);
      if (fresh) {
        const updated = writeSdfPartialCharges(m.data, fresh);
        if (updated) m.data = updated;
        charges = fresh;
      }
    }
    if (charges) m.atomCharges = charges;
  }
};

/**
 * Push the molecule-level `atomCharges` array down into each per-atom
 * `charge` field. Required because `rehydratePartialChargesFromSdf` only
 * populates the molecule-level array, but downstream consumers
 * (MD electrostatics, dipole-current sampling for the IR spectrum, the
 * chamber's atom Charge context-menu field) read `atom.charge` directly.
 *
 * Idempotent: re-applying with the same array is a no-op.
 * No-op for molecules without `atomCharges` or `atoms`.
 */
export const applyAtomChargesToAtoms = <
  T extends { atomCharges?: number[]; atoms?: Array<{ charge?: number; chargeSet?: boolean }> },
>(
  molecules: T[] | undefined | null,
): void => {
  if (!molecules) return;
  for (const m of molecules) {
    if (!m.atomCharges || !m.atoms) continue;
    const n = Math.min(m.atomCharges.length, m.atoms.length);
    for (let i = 0; i < n; i++) {
      const c = m.atomCharges[i];
      // A user-set charge (Atom.chargeSet) is project data: never overwrite it.
      if (Number.isFinite(c) && !m.atoms[i].chargeSet) m.atoms[i].charge = c;
    }
  }
};
